package com.karyadina.ui.screens

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.CameraAlt
import androidx.compose.material.icons.filled.ContentCopy
import androidx.compose.material.icons.filled.Description
import androidx.compose.material.icons.filled.FlashOn
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.unit.dp
import com.karyadina.service.OCRService
import com.karyadina.service.QuickNotesService
import com.karyadina.model.QuickNote
import com.karyadina.model.NoteType
import com.karyadina.util.rememberDocumentScannerLauncher
import com.karyadina.util.rememberImageBitmapFromUri
import com.karyadina.util.currentTimeMillis
import com.karyadina.util.rememberCameraPermissionLauncher
import kotlinx.coroutines.launch

import org.koin.compose.koinInject

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun OCRScannerScreen(
    onBackClick: () -> Unit,
    onSaveToNote: () -> Unit,
    modifier: Modifier = Modifier
) {
    val ocrService = koinInject<OCRService>()
    val notesService = koinInject<QuickNotesService>()
    val scope = rememberCoroutineScope()
    val clipboardManager = LocalClipboardManager.current
    
    var imageUri by remember { mutableStateOf<String?>(null) }
    var extractedText by remember { mutableStateOf("") }
    var isProcessing by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    val scannerLauncher = rememberDocumentScannerLauncher { uri ->
        if (uri != null) {
            imageUri = uri
            isProcessing = true
            errorMessage = null
            scope.launch {
                val result = ocrService.extractText(uri)
                if (result.isSuccess) {
                    extractedText = result.getOrDefault("")
                } else {
                    errorMessage = "Failed to extract text: ${result.exceptionOrNull()?.message}"
                }
                isProcessing = false
            }
        }
    }

    val cameraPermissionLauncher = rememberCameraPermissionLauncher { isGranted ->
        if (isGranted) {
             scannerLauncher.launch()
        } else {
             errorMessage = "Camera permission required to scan."
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Magic Text Scanner") },
                navigationIcon = {
                    IconButton(onClick = onBackClick) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, "Back")
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = modifier
                .fillMaxSize()
                .padding(padding)
                .verticalScroll(rememberScrollState())
                .padding(16.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            if (imageUri == null) {
                // Empty State / Start Scanning
                Spacer(Modifier.height(48.dp))
                Icon(
                    Icons.Filled.CameraAlt,
                    contentDescription = null,
                    modifier = Modifier.size(120.dp),
                    tint = MaterialTheme.colorScheme.primaryContainer
                )
                Text(
                    "Scan any document to extract text instantly.",
                    style = MaterialTheme.typography.bodyLarge,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                Spacer(Modifier.height(24.dp))
                Button(
                    onClick = { cameraPermissionLauncher.launch() },
                    modifier = Modifier.height(56.dp)
                ) {
                    Icon(Icons.Filled.CameraAlt, null)
                    Spacer(Modifier.width(8.dp))
                    Text("Start Camera Scan")
                }
                
                if (errorMessage != null) {
                    Spacer(Modifier.height(16.dp))
                    Text(
                        text = "$errorMessage Please grant camera permission in App Settings.",
                        color = MaterialTheme.colorScheme.error,

                        style = MaterialTheme.typography.bodyMedium,
                        modifier = Modifier.padding(horizontal = 16.dp)
                    )
                }

            } else {
                // Result View
                
                // Image Preview
                ElevatedCard(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(200.dp)
                ) {
                    val bitmap = rememberImageBitmapFromUri(imageUri!!)
                    if (bitmap != null) {
                        Image(
                            bitmap = bitmap,
                            contentDescription = "Scanned Image",
                            contentScale = ContentScale.Crop,
                            modifier = Modifier.fillMaxSize()
                        )
                    } else {
                        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                            CircularProgressIndicator()
                        }
                    }
                }
                
                if (isProcessing) {
                    LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
                    Text("Extracting text...", style = MaterialTheme.typography.labelMedium)
                }
                
                if (errorMessage != null) {
                    Card(
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.errorContainer)
                    ) {
                        Text(
                            text = errorMessage!!,
                            color = MaterialTheme.colorScheme.onErrorContainer,
                            modifier = Modifier.padding(16.dp)
                        )
                    }
                }
                
                // Extracted Text Area
                OutlinedTextField(
                    value = extractedText,
                    onValueChange = { extractedText = it },
                    label = { Text("Extracted Text") },
                    modifier = Modifier.fillMaxWidth().height(300.dp),
                    shape = MaterialTheme.shapes.medium
                )
                
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceEvenly
                ) {
                    IconButton(onClick = { 
                        clipboardManager.setText(AnnotatedString(extractedText)) 
                    }) {
                        Icon(Icons.Filled.ContentCopy, "Copy")
                    }
                    
                
                    FilledTonalButton(onClick = { 
                        if (extractedText.isNotBlank()) {
                            // Generate Title (First line or generic)
                            val title = extractedText.lines().firstOrNull()?.take(50) ?: "Scanned Document"
                            
                            notesService.createNote(
                                QuickNote(
                                    id = "", // Generated by service
                                    title = title,
                                    content = extractedText,
                                    color = "#ecdd00fa", // Default white/gray
                                    isPinned = false,
                                    isArchived = false,
                                    tags = listOf("OCR", "Scanned"),
                                    createdAt = currentTimeMillis(),
                                    updatedAt = currentTimeMillis(),
                                    noteType = NoteType.TEXT.name,
                                    checklistItems = emptyList(),
                                    subject = null
                                )
                            )
                            onSaveToNote() 
                        }
                    }) {
                        Icon(Icons.Filled.Description, null)
                        Spacer(Modifier.width(4.dp))
                        Text("Save Note")
                    }
                }
                
                TextButton(onClick = { cameraPermissionLauncher.launch() }) {
                    Text("Scan Another")
                }
            }
        }
    }
}
